<template>
  <div class="import-preview-ui">
    <VSpinner v-if="isLoading" class="centered-block" />
    <VFallbackText v-else-if="hasError" class="centered"
      >Something went wrong while rendering preview</VFallbackText
    >
    <VFallbackText v-else-if="!csvData.length" class="centered">No data to preview</VFallbackText>
    <div v-else class="import-preview-ui__table-wrapper">
      <table class="import-preview-ui__table">
        <thead class="import-preview-ui__thead">
          <tr class="import-preview-ui__tr">
            <th v-for="(header, index) in csvHeaders" :key="index" class="import-preview-ui__th">
              <VDropdown
                v-model="csvHeaders[index]"
                :options="headerOptions"
                :class="{ error: isHeaderDuplicated(header) }"
                arrow-icon-class-name="fr fr-chevron-down"
                floating
              />
              <span class="import-preview-ui__ignore">
                <VCheckbox
                  v-model="ignoredColumnIndexes[index]"
                  label="ignore"
                  class="import-preview-column-ignore"
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="import-preview-ui__tbody">
          <tr v-for="(row, rowIndex) in csvData" :key="rowIndex" class="import-preview-ui__tr">
            <td
              v-for="(cell, cellIndex) in row"
              :key="cellIndex"
              :class="[
                'import-preview-ui__td',
                ignoredColumnIndexes[cellIndex] && 'import-preview-ui__td--ignored',
              ]"
            >
              {{ cell }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { parse } from 'papaparse';

  // Types
  import type {
    TImportParser,
    TImportParserStepResult,
    TImportColumnIndexes,
    TIgnoredColumnIndexes,
  } from '~/shared/import/types';
  import type { TImportMap } from '~/shared/import/map/types';
  import type { IVDropdownOption } from '~/misc/v-dropdown/types';
  import { EImportParamKey, EMappingVariant } from '~/shared/import/types';

  // Config
  import {
    HEADER_OPTIONS,
    TABLE_HEADER_LISTS,
    DEFAULT_IGNORED_COLUMNS,
  } from '~/shared/import/config';
  import { VISIBLE_ROWS_COUNT } from '~/shared/import/preview/config';

  // Components
  import VDropdown from '~/misc/v-dropdown';
  import VCheckbox from '~/misc/v-checkbox';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VSpinner from '~/misc/v-spinner/VSpinner.vue';

  const props = defineProps({
    file: {
      type: Object as PropType<File | null>,
      default: null,
    },
    selectedMap: {
      type: Object as PropType<TImportMap | null>,
      default: null,
    },
    variant: {
      type: String as PropType<EMappingVariant>,
      required: true,
    },
    isCustomerRefReplaced: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits<{
    (e: 'index-updated', indexes: TImportColumnIndexes): void;
    (e: 'set-is-customer-ref-replaced', state: boolean): void;
  }>();

  const chunk: Ref<string[][]> = ref<string[][]>([]);
  const csvData: Ref<string[][]> = ref<string[][]>([]);
  const csvHeaders: Ref<string[]> = ref<string[]>(TABLE_HEADER_LISTS[props.variant]);
  const ignoredColumnIndexes: Ref<TIgnoredColumnIndexes> = ref<TIgnoredColumnIndexes>({});
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);

  const headerOptions: ComputedRef<IVDropdownOption<string, string>[]> = computed<
    IVDropdownOption<string, string>[]
  >(() => HEADER_OPTIONS[props.variant]);

  const headerDuplicateHashMap: ComputedRef<Record<string, number>> = computed<
    Record<string, number>
  >(() => {
    const duplicateMap: Record<string, number> = {};

    for (const [index, item] of csvHeaders.value.entries()) {
      if (ignoredColumnIndexes.value[index]) continue;

      if (duplicateMap[item]) {
        duplicateMap[item]++;
      } else {
        duplicateMap[item] = 1;
      }
    }

    return duplicateMap;
  });

  const customerRefIndex: ComputedRef<number> = computed<number>(() =>
    csvHeaders.value.findIndex((header: string) => header === EImportParamKey.CUSTOMER_REFERENCE)
  );

  const setCsvData = (data: string[][]) => {
    csvData.value = data;
  };

  const setCsvHeaders = (data: string[]) => {
    csvHeaders.value = data;
  };

  const resetChunk = () => {
    chunk.value = [];
  };

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setHasError = (state: boolean) => {
    hasError.value = state;
  };

  const resetIgnoredColumnIndexes = () => {
    ignoredColumnIndexes.value = {};

    setDefaultIgnoredColumns();
  };

  const setDefaultIgnoredColumns = () => {
    DEFAULT_IGNORED_COLUMNS.forEach((column: EImportParamKey) => {
      const columnIndex: number = csvHeaders.value.findIndex((header: string) => header === column);
      if (columnIndex >= 0) {
        ignoredColumnIndexes.value[columnIndex] = true;
      }
    });
  };

  const reset = () => {
    csvData.value = [];

    resetIgnoredColumnIndexes();
    setIsLoading(false);
  };

  const isHeaderDuplicated = (value: string): boolean => headerDuplicateHashMap.value[value] > 1;

  const parseCSV = async (file: File) => {
    try {
      setIsLoading(true);

      await handleParse(file);
    } catch (error) {
      setHasError(true);

      /* eslint-disable no-console */
      console.error('Error parsing CSV:', error);
      /* eslint-enable no-console */
    } finally {
      setIsLoading(false);
    }
  };

  const handleParse = (receivedFile: File) => {
    setIsLoading(true);

    parse(receivedFile, {
      skipEmptyLines: true,
      step: (results: TImportParserStepResult<string[]>, parser: TImportParser) => {
        chunk.value.push(results.data);

        if (chunk.value.length === VISIBLE_ROWS_COUNT) {
          parser.pause();
          setCsvData(chunk.value);
          resetChunk();
        }
      },
      complete: async () => {
        await nextTick();

        if (hasError.value) return;

        setCsvData(chunk.value);
        resetChunk();
      },
    });
  };

  const indexesToHeaders = (data: TImportColumnIndexes): string[] =>
    Object.keys(data).reduce((acc: string[], key: string) => {
      const index: number = data[key];

      acc[index] = key;

      return acc;
    }, []);

  const headersToIndexes = (data: (string | null)[]): TImportColumnIndexes =>
    data.reduce((acc: TImportColumnIndexes, item: string | null, index: number) => {
      if (item !== null) {
        acc[item] = index;
      }

      return acc;
    }, {});

  const emitSetIsCustomerRefReplaced = () =>
    emit('set-is-customer-ref-replaced', !!ignoredColumnIndexes.value[customerRefIndex.value]);

  watch(
    () => props.file,
    (file: File | null) => (file ? parseCSV(file) : reset()),
    { immediate: true }
  );

  watch(
    () => props.selectedMap,
    (map: TImportMap | null) => {
      if (!map) return;

      setCsvHeaders(indexesToHeaders(map.config));
    }
  );

  watch(
    () => props.variant,
    (variant: EMappingVariant) => {
      setCsvHeaders(TABLE_HEADER_LISTS[variant]);
      resetIgnoredColumnIndexes();
    }
  );

  watch(
    () => csvHeaders.value,
    (data: string[]) => {
      emit('index-updated', headersToIndexes(data));
      emitSetIsCustomerRefReplaced();
    },
    {
      immediate: true,
      deep: true,
    }
  );

  watch(
    () => ignoredColumnIndexes.value,
    (newIgnoredIndexes: TIgnoredColumnIndexes) => {
      // Replace ignored columns with null
      const newCsvHeaders: TImportColumnIndexes = headersToIndexes(
        csvHeaders.value.map((item: string, index: number) =>
          newIgnoredIndexes[index] ? null : item
        )
      );

      emit('index-updated', newCsvHeaders);
      emitSetIsCustomerRefReplaced();
    },
    {
      immediate: true,
      deep: true,
    }
  );

  watch(
    () => props.isCustomerRefReplaced,
    (state: boolean) => {
      if (customerRefIndex.value < 0) return;

      ignoredColumnIndexes.value[customerRefIndex.value] = state;
    }
  );
</script>
